import { configureStore } from "@reduxjs/toolkit";
import { setupListeners } from "@reduxjs/toolkit/query";
import { combineReducers } from "redux";
import { persistStore, persistReducer, FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER } from "redux-persist";
import storage from "redux-persist/lib/storage";
import userReducer from "./userSlice";
import alertReducer from "./alertSlice";
import loaderReducer from "./loaderSlice";
import customerReport from "./customer/reportingTeamSlice";
import customerSocial from "./customer/socialLinkSlice";
import addressSlice from "./customer/addressSlice";
import contactPersons from "./customer/contactPersonsSlice";
import customerArtifact from "./customer/artifactSlice";
import customerServices from "./customer/servicesSlice";
import profileReducer from "./companyProfileSlice";
import permissionReducer from "./permissionSlice";

import {
  customerReportingTeamDataApi,
  customerTasksTableDataApi,
  customerServiceCategoryApi,
} from "./customer/customerSelect";

import {
  customerTableShowUpdate,
  customerTableDropAdd,
  randomUser,
  custTableHeadFieldDynAddUp,
  custTableAddFieldHeader,
  companyPermissionsData,
} from "./customer/customerTable";
import { customerHeaderApi, headerConfigurationView } from "./customer/customerListHeader";
import customerNotesReducer from "./customer/customerNotesSlice";
import customerTaskReduces from "./customer/customerTaskSlice";
import vendorTeamSlice from "./vendor/vendorTeamSlice";
import customerTasksPodSlice from "./customer/taskPodSlice";
import customerTaskEventReducer from "./customer/customerTaskEventSlice";
import vendorSocialSlice from "./vendor/vendorSocialSlice";
import { companyListApi, companyCommonDataApi } from "./company/companySelector";
import {
  customerTaskTableShowUpdate,
  TaskGetOrgAccording,
  customerTaskTableDropAdd,
  customerSocialShowUpdate,
  customerDocumentUpdate,
  customerReportingTeamUpdate,
  customerTaskDueTableShowUpdate,
  customerTaskCloseTableShowUpdate,
  customerContactTableShowUpdate,
  customerTaskPodTableShowUpdate,
  customerTaskActivityCount,
  companyTableShowUpdate,
  customerServiceTableShowUpdate,
  customerAddressTableShowUpdate,
  customerAllAddressTableShowUpdate,
  customerTaskHeaderApi,
  customerCompanyDataApi,
  customerAddressSingleDataApi,
  customerNotesTableDataApi,
  customerNotesDataTableShowUpdate,
  customerActivityDataTableShowUpdate,
  userDataTableShowUpdate,
  manageListApi,
  dynamicDataTableShowUpdate,
  dynamicHeader,
  dynamicEditValue,
  getAllData,
  companyDelegation,
  customerTableDelegation,
  companyAddressTableShowUpdate,
  companyAddressSingleDataApi,
  vendorList,
  vendorSocial,
  vendorContact,
  vendorAllAddress,
  customerGroup,
  deleteTableCategoriesData,
  VendorsAPI,
} from "./customer/customerTaskTable";
import { companyManagePermissions, PermissionModuleCompany } from "./company/companypermissions";
import { managePermissions, PermissionsAddUpdate, selectPermission, PermissionModule } from "./users/permissions";
import { manageGroups } from "./users/groups";
import { manageGroupsCompany } from "./customer/groups";
import { funnelApis } from "./customer/funnelSlice";
import { orgGroupAddUpdate, orgGroupAddUpdateMember } from "./users/Organizations";
import { EnumFieldGetApi } from "./enumApi";
import { coaAllLevelData } from "./accounting/chartOfAccount";
import { notificationApi } from "../components/userProfile/GetAllNotification";
import { vendorCreateApi, vouchersTypeApi } from "./vendor/vendorApis";
import { pushAlert } from "./alertSlice";
// import { ActivityApis } from "../components/ActivityLogs/ActivityAPIS"; // commented as it is not in use as Talib said
import { companyDashboard } from "./company/companyDashboard";
import { ActivityApis } from "../components/ActivityLogs/AuditLogAPIS";

const persistConfig = {
  key: "root",
  storage,
  whitelist: ["customerTableShowUpdate", "customerTableDropAdd", "customerHeaderApi"],
};

const allReducers = combineReducers({
  user: userReducer,
  companyPermission: permissionReducer,
  alert: alertReducer,
  loader: loaderReducer,
  profile: profileReducer,
  customerReportingTeam: customerReport,
  customerSocialLink: customerSocial,
  customerAddresses: addressSlice,
  contactPersons: contactPersons,
  customerArtifact: customerArtifact,
  customerServices: customerServices,
  notes: customerNotesReducer,
  tasks: customerTaskReduces,
  activity: customerTaskEventReducer,
  taskPod: customerTasksPodSlice,
  vendors: vendorTeamSlice,
  vendorSocial: vendorSocialSlice,
  [companyListApi.reducerPath]: companyListApi.reducer,
  [customerHeaderApi.reducerPath]: customerHeaderApi.reducer,
  [headerConfigurationView.reducerPath]: headerConfigurationView.reducer,
  [companyCommonDataApi.reducerPath]: companyCommonDataApi.reducer,
  [companyDashboard.reducerPath]: companyDashboard.reducer,

  [customerTableDropAdd.reducerPath]: customerTableDropAdd.reducer,
  [randomUser.reducerPath]: randomUser.reducer,
  [custTableAddFieldHeader.reducerPath]: custTableAddFieldHeader.reducer,
  [customerTableShowUpdate.reducerPath]: customerTableShowUpdate.reducer,
  [customerTaskTableShowUpdate.reducerPath]: customerTaskTableShowUpdate.reducer,
  [TaskGetOrgAccording.reducerPath]: TaskGetOrgAccording.reducer,

  [EnumFieldGetApi.reducerPath]: EnumFieldGetApi.reducer,

  [customerTaskTableDropAdd.reducerPath]: customerTaskTableDropAdd.reducer,
  [customerServiceCategoryApi.reducerPath]: customerServiceCategoryApi.reducer,
  [customerSocialShowUpdate.reducerPath]: customerSocialShowUpdate.reducer,

  [customerDocumentUpdate.reducerPath]: customerDocumentUpdate.reducer,
  [customerReportingTeamUpdate.reducerPath]: customerReportingTeamUpdate.reducer,
  [customerTaskDueTableShowUpdate.reducerPath]: customerTaskDueTableShowUpdate.reducer,
  [customerTaskCloseTableShowUpdate.reducerPath]: customerTaskCloseTableShowUpdate.reducer,
  [customerContactTableShowUpdate.reducerPath]: customerContactTableShowUpdate.reducer,
  [customerTaskPodTableShowUpdate.reducerPath]: customerTaskPodTableShowUpdate.reducer,
  [customerTaskActivityCount.reducerPath]: customerTaskActivityCount.reducer,
  [companyTableShowUpdate.reducerPath]: companyTableShowUpdate.reducer,
  [customerServiceTableShowUpdate.reducerPath]: customerServiceTableShowUpdate.reducer,
  [customerAddressTableShowUpdate.reducerPath]: customerAddressTableShowUpdate.reducer,
  [customerAllAddressTableShowUpdate.reducerPath]: customerAllAddressTableShowUpdate.reducer,

  [customerTaskHeaderApi.reducerPath]: customerTaskHeaderApi.reducer,
  [customerCompanyDataApi.reducerPath]: customerCompanyDataApi.reducer,
  [customerAddressSingleDataApi.reducerPath]: customerAddressSingleDataApi.reducer,
  [customerNotesTableDataApi.reducerPath]: customerNotesTableDataApi.reducer,
  [customerNotesDataTableShowUpdate.reducerPath]: customerNotesDataTableShowUpdate.reducer,
  [customerActivityDataTableShowUpdate.reducerPath]: customerActivityDataTableShowUpdate.reducer,
  [userDataTableShowUpdate.reducerPath]: userDataTableShowUpdate.reducer,
  [manageListApi.reducerPath]: manageListApi.reducer,
  [dynamicDataTableShowUpdate.reducerPath]: dynamicDataTableShowUpdate.reducer,
  [dynamicHeader.reducerPath]: dynamicHeader.reducer,
  [dynamicEditValue.reducerPath]: dynamicEditValue.reducer,
  [getAllData.reducerPath]: getAllData.reducer,
  [managePermissions.reducerPath]: managePermissions.reducer,
  [PermissionsAddUpdate.reducerPath]: PermissionsAddUpdate.reducer,
  [selectPermission.reducerPath]: selectPermission.reducer,
  [PermissionModule.reducerPath]: PermissionModule.reducer,
  [PermissionModuleCompany.reducerPath]: PermissionModuleCompany.reducer,
  [manageGroups.reducerPath]: manageGroups.reducer,
  [manageGroupsCompany.reducerPath]: manageGroupsCompany.reducer,
  [funnelApis.reducerPath]: funnelApis.reducer,
  [orgGroupAddUpdate.reducerPath]: orgGroupAddUpdate.reducer,
  [orgGroupAddUpdateMember.reducerPath]: orgGroupAddUpdateMember.reducer,
  [companyDelegation.reducerPath]: companyDelegation.reducer,
  [customerTableDelegation.reducerPath]: customerTableDelegation.reducer,
  [companyAddressTableShowUpdate.reducerPath]: companyAddressTableShowUpdate.reducer,
  [companyAddressSingleDataApi.reducerPath]: companyAddressSingleDataApi.reducer,
  [vendorList.reducerPath]: vendorList.reducer,
  [vendorSocial.reducerPath]: vendorSocial.reducer,
  [vendorContact.reducerPath]: vendorContact.reducer,
  [vendorAllAddress.reducerPath]: vendorAllAddress.reducer,

  [customerGroup.reducerPath]: customerGroup.reducer,
  [deleteTableCategoriesData.reducerPath]: deleteTableCategoriesData.reducer,
  [companyPermissionsData.reducerPath]: companyPermissionsData.reducer,
  [companyManagePermissions.reducerPath]: companyManagePermissions.reducer,
  [coaAllLevelData.reducerPath]: coaAllLevelData.reducer,
  [notificationApi.reducerPath]: notificationApi.reducer,
  [vouchersTypeApi.reducerPath]: vouchersTypeApi.reducer,
  [vendorCreateApi.reducerPath]: vendorCreateApi.reducer,
  [VendorsAPI.reducerPath]: VendorsAPI.reducer,
  [ActivityApis.reducerPath]: ActivityApis.reducer,
  // Add other reducers here
});

const appStore = configureStore({
  reducer: allReducers,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    })
      .concat(customerHeaderApi.middleware)
      .concat(headerConfigurationView.middleware)
      .concat(companyListApi.middleware)
      .concat(companyCommonDataApi.middleware)

      .concat(customerTableShowUpdate.middleware)
      .concat(customerTableDropAdd.middleware)
      .concat(randomUser.middleware)
      .concat(custTableAddFieldHeader.middleware)

      .concat(customerTaskTableShowUpdate.middleware)
      .concat(TaskGetOrgAccording.middleware)
      .concat(customerTaskTableDropAdd.middleware)

      .concat(EnumFieldGetApi.middleware)
      .concat(customerSocialShowUpdate.middleware)

      .concat(customerDocumentUpdate.middleware)
      .concat(customerReportingTeamUpdate.middleware)
      .concat(customerTaskDueTableShowUpdate.middleware)
      .concat(customerTaskCloseTableShowUpdate.middleware)
      .concat(customerContactTableShowUpdate.middleware)
      .concat(customerTaskPodTableShowUpdate.middleware)
      .concat(customerTaskActivityCount.middleware)
      .concat(companyTableShowUpdate.middleware)
      .concat(customerServiceTableShowUpdate.middleware)
      .concat(customerAddressTableShowUpdate.middleware)
      .concat(customerAllAddressTableShowUpdate.middleware)

      .concat(customerTaskHeaderApi.middleware)
      .concat(customerCompanyDataApi.middleware)
      .concat(customerAddressSingleDataApi.middleware)
      .concat(customerNotesTableDataApi.middleware)
      .concat(customerNotesDataTableShowUpdate.middleware)
      .concat(customerActivityDataTableShowUpdate.middleware)
      .concat(userDataTableShowUpdate.middleware)
      .concat(manageListApi.middleware)
      .concat(dynamicDataTableShowUpdate.middleware)
      .concat(dynamicHeader.middleware)
      .concat(dynamicEditValue.middleware)
      .concat(getAllData.middleware)
      .concat(customerServiceCategoryApi.middleware)
      .concat(managePermissions.middleware)
      .concat(PermissionsAddUpdate.middleware)
      .concat(selectPermission.middleware)
      .concat(PermissionModule.middleware)
      .concat(PermissionModuleCompany.middleware)
      .concat(manageGroups.middleware)
      .concat(manageGroupsCompany.middleware)
      .concat(funnelApis.middleware)
      .concat(orgGroupAddUpdate.middleware)
      .concat(orgGroupAddUpdateMember.middleware)
      .concat(companyDelegation.middleware)
      .concat(customerTableDelegation.middleware)

      .concat(companyAddressTableShowUpdate.middleware)
      .concat(companyAddressSingleDataApi.middleware)
      .concat(notificationApi.middleware)
      .concat(vendorList.middleware)
      .concat(vendorSocial.middleware)
      .concat(vendorContact.middleware)
      .concat(vendorAllAddress.middleware)

      .concat(customerGroup.middleware)
      .concat(companyPermissionsData.middleware)
      .concat(companyManagePermissions.middleware)
      .concat(coaAllLevelData.middleware)
      .concat(vouchersTypeApi.middleware)
      .concat(vendorCreateApi.middleware)
      .concat(VendorsAPI.middleware)
      .concat(ActivityApis.middleware)

      .concat(companyDashboard.middleware),
});

setupListeners(appStore.dispatch);

// const persistor = persistStore(appStore);
const persistor = appStore;

export { appStore, persistor };
